import type { ReactNode } from 'react'
import { Box, Grid2 as Grid, Paper, Stack, Typography } from '@mui/material'

export const SUMMARY_EMPTY = '—'

export interface SummaryField {
  label: string
  // React.ReactNode so callers can pass a link, badge, formatter, etc.
  value: ReactNode
  // Full-width row (spans all columns). Handy for long descriptions.
  fullWidth?: boolean
}

export interface SummaryInfoCardProps {
  title: string
  icon?: ReactNode
  fields: SummaryField[]
  // md size: 3-column = 4, 2-column = 6. Defaults to 3.
  columns?: 2 | 3
  // `creation` = grey.50 background (matches Step2/Step4 legacy cards).
  // `detail`   = plain white surface (drawer context).
  variant?: 'creation' | 'detail'
  testId?: string
}

/**
 * Shared "labeled fields" summary card used in Rate Card creation Step 2 & 4
 * (Partner & Dates / Pricing Parameters) and in the detail drawer. Column
 * count and background flip via props so the same layout serves both flows.
 */
const SummaryInfoCard: React.FC<SummaryInfoCardProps> = ({
  title,
  icon,
  fields,
  columns = 3,
  variant = 'creation',
  testId
}) => {
  const colSize = columns === 2 ? 6 : 4
  const isDetail = variant === 'detail'
  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2,
        mb: isDetail ? 2 : 3,
        bgcolor: isDetail ? 'background.paper' : 'grey.50'
      }}
      data-testid={testId}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
        {icon}
        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {fields.map((f, idx) => (
          <Grid
            key={`${f.label}-${idx}`}
            size={{ xs: 12, md: f.fullWidth ? 12 : colSize }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block' }}>
              {f.label}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                mt: 0.25,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'
              }}>
              {f.value === '' || f.value === null || f.value === undefined
                ? SUMMARY_EMPTY
                : f.value}
            </Typography>
          </Grid>
        ))}
        {/* Pad the last row when field count doesn't fill the grid, so the
            two-row layout stays aligned. Only needed when columns=3 and a
            non-fullWidth trailing count leaves a gap; a single spacer is
            enough because Grid wraps naturally. */}
        {(() => {
          const nonFullWidthCount = fields.filter(f => !f.fullWidth).length
          const remainder = nonFullWidthCount % columns
          if (remainder === 0) return null
          const padCount = columns - remainder
          return Array.from({ length: padCount }).map((_, i) => (
            <Grid key={`pad-${i}`} size={{ xs: 12, md: colSize }}>
              <Box />
            </Grid>
          ))
        })()}
      </Grid>
    </Paper>
  )
}

export default SummaryInfoCard
